import type { SubscriptionItem } from 'modules/billing/store/models/Subscription'
import type Subscription from 'modules/billing/store/models/Subscription'

type Currency = 'EUR' | 'USD' | 'GBP' | 'MXN' | 'BRL' | 'COP' | 'ARS'
interface ChargebeeBase {
  currencyCode: Currency
  period: number
  periodUnit: 'day' | 'week' | 'month' | 'year' | 'not_applicable'
  pricingModel: 'flat_fee' | 'per_unit' | 'tiered' | 'volume' | 'stairstep' | 'talk_to_sales'
  price: number | null
  chargeType: 'recurring' | 'non_recurring'
  tiers?: { startingUnit?: number; endingUnit?: number }[]
}

export type PlanStatus = 'current' | 'free' | 'legacy'

export interface ChargebeeAddon extends ChargebeeBase {
  addonId: string
}

export interface ChargebeePlan extends ChargebeeBase {
  planId: string
}

export interface ChargebeeSubscription {
  plan: ChargebeeAddon | ChargebeePlan
  quantity: number
}

export interface SimpleChargebeePlanSubscription {
  planId: string
}
export interface SimpleChargebeeAddonSubscription {
  addonId: string
}

export interface SimpleChargebeeSubscription {
  plan: SimpleChargebeePlanSubscription | SimpleChargebeeAddonSubscription
  quantity: number
}

interface RegionPlanCommon {
  features: {
    title: string
    tooltip?: string
  }[]
}

interface RegionPlan extends RegionPlanCommon {
  chargebeePlans: ChargebeePlan[]
}

interface RegionAddon extends RegionPlanCommon {
  chargebeeAddons: ChargebeeAddon[]
}

export interface CorePlan {
  title: string
  regions: RegionPlan
  inheritFeatures: {
    title: string
    regions: RegionPlan
  }
}

export interface NormalPlan {
  title: string
  description?: string
  internalTitle: string
  parentModule: string
  regions: RegionAddon
  inheritFeatures: {
    title: string
    regions: RegionAddon
  }
}

export type ExtraAddon = {
  plan: NormalPlan
  chargebeeAddons: ChargebeeAddon[]
  isIncluded: boolean
}

export type ChargebeeBundle = {
  corePlan: CorePlan
  extraAddon?: ExtraAddon[]
  extraAddons?: NormalPlan[]
  extraAddonActive: boolean
  chargebeeAddons: (ChargebeeAddon | ChargebeePlan)[]
  minimumSeats: number
  title: string
  internalTitle: string
  description: string
  normalPlans: NormalPlan[]
  mostPopular?: boolean
  benefits?: {
    title: string
  }[]
}

type ChargebeeSubscriptionProps = ChargebeeBundle & {
  annually: boolean
  seats: number
}

export type SimpleSubscriptionItem = {
  itemId: string
  quantity: number
}

const isSimpleChargebeePlanSubscription = (
  plan: SimpleChargebeePlanSubscription | SimpleChargebeeAddonSubscription
): plan is SimpleChargebeePlanSubscription => {
  return 'planId' in plan
}

export const getSimpleSubscriptionItems = (
  subscription: SimpleChargebeeSubscription
): SimpleSubscriptionItem => {
  const { plan } = subscription

  const itemId = isSimpleChargebeePlanSubscription(plan) ? plan.planId : plan.addonId

  return { itemId, quantity: subscription.quantity }
}

export const getPlainSelectedPlan = (subscriptions: SimpleChargebeeSubscription[]): string => {
  return JSON.stringify(
    subscriptions.map(
      (subscription: SimpleChargebeeSubscription) => getSimpleSubscriptionItems(subscription).itemId
    )
  )
}

const getPrice = (subscription: { plan: ChargebeeAddon | ChargebeePlan; quantity: number }[]) => {
  const { recurringPrice, oneTimePrice, perSeat } = subscription.reduce(
    (acc, { plan: { chargeType, price, pricingModel }, quantity }) => {
      const numberPrice = price || 0
      const key = chargeType === 'recurring' ? 'recurringPrice' : 'oneTimePrice'
      switch (pricingModel) {
        case 'flat_fee':
          return {
            ...acc,
            ...(key === 'recurringPrice' ? { oneTimePrice: numberPrice } : {}),
            [key]: acc[key] + numberPrice * quantity
          }
        case 'per_unit':
          return {
            ...acc,
            ...(key === 'recurringPrice' ? { perSeat: acc.perSeat + numberPrice } : {}),
            [key]: acc[key] + numberPrice * quantity
          }
      }

      return acc
    },
    { recurringPrice: 0, oneTimePrice: 0, perSeat: 0 }
  )

  return {
    recurringPrice: recurringPrice / 100,
    oneTimePrice: oneTimePrice / 100,
    perSeat: perSeat / 100
  }
}

export const getChargebeeSubscriptionItems = ({
  corePlan,
  extraAddons,
  chargebeeAddons = [],
  annually,
  seats,
  minimumSeats
}: ChargebeeSubscriptionProps): ChargebeeSubscription[] => {
  const subscription = []
  const allPlans = [
    ...(corePlan?.regions?.chargebeePlans || []),
    ...(extraAddons?.length ? extraAddons.map((e) => e.regions.chargebeeAddons).flat() : []),
    ...chargebeeAddons
  ].flat()

  const period = annually ? 'month' : 'year'
  const filteredPlans = allPlans.filter((plan) => plan && plan.periodUnit !== period)
  for (const plan of filteredPlans) {
    subscription.push({
      plan,
      quantity: plan.pricingModel === 'per_unit' ? Math.max(seats || 0, minimumSeats) : 1
    })
  }
  return subscription
}

export const getBundlePrice = (props: ChargebeeSubscriptionProps) => {
  return getPrice(getChargebeeSubscriptionItems({ ...props }))
}

const isHubCurrentSubscription = ({
  subscriptionItemsFromHub = [],
  currentSubscriptionItems = []
}: {
  subscriptionItemsFromHub: SimpleSubscriptionItem[]
  currentSubscriptionItems: SubscriptionItem[]
}) => {
  if (!subscriptionItemsFromHub.length) return -1

  const subscriptionsInBundle = []
  for (const item of currentSubscriptionItems || []) {
    const foundIndex = subscriptionItemsFromHub.findIndex(
      (bundle) => bundle.itemId === item.item_price_id && bundle.quantity === item.quantity
    )

    subscriptionsInBundle.push(foundIndex > -1)
  }

  return subscriptionsInBundle.every(Boolean)
}

export const getCurrentPlanStatus = (
  subscriptionItemsFromHub: SimpleSubscriptionItem[],
  currentSubscription: Subscription
): PlanStatus => {
  if (!currentSubscription.inPaidPlan) return 'free'

  const currentSubscriptionItems = currentSubscription.chargebeeSubscriptionItems

  // FIXME: This is super hacky for hellen in development environment, as she is an exception
  // when dealing with an active subscription.
  if (!currentSubscriptionItems.length) return 'legacy'

  const isSamePlan = isHubCurrentSubscription({
    currentSubscriptionItems,
    subscriptionItemsFromHub
  })

  if (!isSamePlan) return 'legacy'

  return 'current'
}

export const formatPrice = (number: number, currency: string) => {
  if (currency === 'COP') {
    return (
      '$' +
      `${new Intl.NumberFormat(navigator.language, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(number)}`
    )
  }

  return new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(number)
}
